// 1:1 port from age-of-mcp v3 fishing hut — a footprint-native 2x2 shoreline
// plot: a plank jetty deck on pilings carries a small timber hut under a
// wheat-gold (ROOF_FOOD) gable on the back-west corner, with an oversized fish
// rack out on the open deck (a tall beam with three big silvery fish), a canvas
// drying net, cork buoys and a crate. The fisher works the open front-east deck.
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const {
  box, cyl, gable, poles, slab,
  CANVAS, FIRE, ROOF_FOOD, STONE, TIMBER, WOOD, WOOD_DK,
} = styleV3;

const DECK_TOP = 0.42;        // jetty deck surface

// The plank jetty deck on its four corner pilings.
function deck(): Part[] {
  const parts: Part[] = [
    box(1.8, 0.1, 1.8, WOOD, [0, DECK_TOP - 0.05, 0]),
  ];
  for (const [x, z] of [[-0.72, -0.72], [0.72, -0.72], [-0.72, 0.72], [0.72, 0.72]] as const) {
    parts.push(cyl(0.08, 0.09, 0.37, 5, WOOD_DK, [x, 0.185, z]));
  }
  return parts;
}

// The timber hut on the back-west deck corner, `h` of wall.
function hut(h: number): Part {
  return box(0.72, h, 0.72, WOOD_DK, [-0.48, DECK_TOP + h / 2, -0.45]);
}

// The gold gable roof over the hut — the food-chain code (stage 3 only).
function hutRoof(): Part {
  return gable(0.84, 0.84, 0.26, ROOF_FOOD, -0.48, DECK_TOP + 0.5, -0.45, "z");
}

// The hut's dark doorway, opening onto the deck.
function doorway(): Part {
  return box(0.22, 0.34, 0.04, TIMBER, [-0.48, DECK_TOP + 0.17, -0.08]);
}

// The OVERSIZED fish rack on the open east deck — the icon from above: two
// posts, a high beam, three big silvery fish hanging.
function fishRack(): Part[] {
  const parts: Part[] = [
    box(0.05, 0.5, 0.05, WOOD_DK, [0.25, DECK_TOP + 0.25, 0.42]),
    box(0.05, 0.5, 0.05, WOOD_DK, [0.82, DECK_TOP + 0.25, 0.42]),
    box(0.66, 0.05, 0.05, WOOD_DK, [0.535, DECK_TOP + 0.52, 0.42]),
  ];
  for (let i = 0; i < 3; i++) {
    parts.push(box(0.07, 0.22, 0.04, STONE, [0.37 + i * 0.17, DECK_TOP + 0.38, 0.42], [0, 0, 0.08 - i * 0.08]));
  }
  return parts;
}

// The drying net strung on a frame on the north deck.
function net(): Part[] {
  return [
    box(0.05, 0.42, 0.05, WOOD_DK, [0.18, DECK_TOP + 0.21, -0.5]),
    box(0.05, 0.42, 0.05, WOOD_DK, [0.72, DECK_TOP + 0.21, -0.5]),
    box(0.52, 0.36, 0.02, CANVAS, [0.45, DECK_TOP + 0.24, -0.5]),
  ];
}

// Cork buoys and a crate dressing the deck.
function clutter(): Part[] {
  return [
    cyl(0.05, 0.05, 0.09, 6, FIRE, [-0.05, DECK_TOP + 0.045, 0.65]),
    cyl(0.04, 0.04, 0.08, 6, FIRE, [-0.18, DECK_TOP + 0.04, 0.72]),
    box(0.16, 0.14, 0.16, WOOD_DK, [-0.75, DECK_TOP + 0.07, 0.25], [0, 0.35, 0]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.84, 1.84)];
  if (s === 1) return [slab(1.84, 1.84), ...deck(), ...poles(1.6, 1.6, 0.65)];
  if (s === 2) {
    return [
      slab(1.84, 1.84), ...deck(), hut(0.5), doorway(),
      ...fishRack(), ...poles(1.6, 1.6, 1.0),
    ];
  }
  return [
    slab(1.84, 1.84), ...deck(), hut(0.5), hutRoof(), doorway(),
    ...fishRack(), ...net(), ...clutter(),
  ];
}

export const fishingHutV3 = defineModel({
  id: "fishing-hut-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Fishing hut (v3)",
  tags: ["building","fishing","hut","low-poly","age-of-mcp","v3","water"],
  summary: "Low-poly fishing hut.",
  description: "A timber fishing hut on a jetty over the water, catching fish.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [0.45, 0.42, 0.72], facing: 0 },
  ],
  interactions: [
    { id: "fish", label: "Working the deck", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
